import { useEffect, useRef, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { formatLastSeen } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Send, ArrowLeft, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import TypingDots from "@/components/TypingDots";
import TranslateButton from "@/components/TranslateButton";

const TYPING_HEARTBEAT_MS = 3000;

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const lastTypedRef = useRef(0);

  const loadThreads = async (silent = false) => {
    try {
      const r = await api.get("/admin/threads");
      setThreads(r.data);
    } catch (e) { if (!silent) toast.error(getApiError(e)); }
  };

  const loadThread = async (user_id, silent = false) => {
    try {
      const r = await api.get(`/admin/threads/${user_id}`);
      setActiveThread(r.data);
      if (!silent) loadThreads(true);
    } catch (e) { toast.error(getApiError(e)); }
  };

  useEffect(() => { loadThreads(); }, []);

  // Refresh thread list every 5s for live typing/last-seen on the list view.
  useEffect(() => {
    if (activeUserId) return;
    const id = setInterval(() => { if (!document.hidden) loadThreads(true); }, 5000);
    return () => clearInterval(id);
  }, [activeUserId]);

  useEffect(() => {
    if (!activeUserId) return;
    loadThread(activeUserId);
  }, [activeUserId]);

  // Poll for new messages every 5s while a thread is open.
  useEffect(() => {
    if (!activeUserId) return;
    const id = setInterval(() => {
      if (!document.hidden) loadThread(activeUserId, true);
    }, 5000);
    return () => clearInterval(id);
  }, [activeUserId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [activeThread?.messages?.length]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !activeUserId) return;
    setSending(true);
    try {
      await api.post(`/admin/threads/${activeUserId}/reply`, { body: text });
      setReply("");
      loadThread(activeUserId);
    } catch (e) { toast.error(getApiError(e)); }
    finally { setSending(false); }
  };

  const onChangeReply = (e) => {
    setReply(e.target.value);
    if (!activeUserId) return;
    const now = Date.now();
    if (now - lastTypedRef.current > TYPING_HEARTBEAT_MS) {
      lastTypedRef.current = now;
      api.post(`/admin/threads/${activeUserId}/typing`).catch(() => {});
    }
  };

  // List view.
  if (!activeUserId) {
    return (
      <Card className="mt-4 border-black/5" data-testid="admin-threads-card">
        <CardContent className="p-0">
          {threads.length === 0 && (
            <p className="p-6 text-sm text-[color:var(--text-secondary)] text-center" data-testid="admin-threads-empty">
              No member conversations yet.
            </p>
          )}
          <ul className="divide-y divide-black/5">
            {threads.map((t) => {
              const seen = formatLastSeen(t.member_last_seen);
              return (
                <li key={t.user_id}>
                  <button
                    onClick={() => setActiveUserId(t.user_id)}
                    className="w-full text-left p-4 hover:bg-[color:var(--bg-secondary)] transition-colors flex items-start gap-3"
                    data-testid={`admin-thread-${t.user_id}`}
                  >
                    <div className="h-9 w-9 rounded-full bg-[color:var(--fire-red)]/10 text-[color:var(--fire-red)] flex items-center justify-center font-serif">
                      {(t.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <strong className="truncate">{t.name}</strong>
                        <span className="text-[10px] text-[color:var(--text-secondary)] whitespace-nowrap">
                          {new Date(t.last_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-xs text-[color:var(--text-secondary)] truncate">{t.email}</div>
                      <p className="text-sm text-[color:var(--text-secondary)] truncate mt-1">
                        <span className="opacity-60">{t.last_sender === "admin" ? "You: " : ""}</span>
                        {t.last_message}
                      </p>
                      <div className="text-[10px] mt-1" data-testid={`admin-thread-presence-${t.user_id}`}>
                        {t.member_typing ? (
                          <span className="flex items-center gap-1.5 text-[color:var(--bright-green)]">
                            typing <TypingDots />
                          </span>
                        ) : seen ? (
                          <span className="text-[color:var(--text-secondary)]">last seen {seen}</span>
                        ) : null}
                      </div>
                    </div>
                    {t.unread > 0 && (
                      <Badge className="bg-[color:var(--fire-red)] text-white" data-testid={`admin-thread-unread-${t.user_id}`}>
                        {t.unread}
                      </Badge>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    );
  }

  // Conversation view.
  const u = activeThread?.user || {};
  const msgs = activeThread?.messages || [];
  const presence = activeThread?.presence || { last_seen: null, is_typing: false };
  const seen = formatLastSeen(presence.last_seen);
  return (
    <Card className="mt-4 border-black/5" data-testid="admin-thread-view">
      <CardContent className="p-0">
        <div className="flex items-center gap-3 p-4 border-b border-black/5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setActiveUserId(null); setActiveThread(null); }}
            data-testid="admin-thread-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />All threads
          </Button>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg truncate">{u.name}</div>
            <div className="text-xs text-[color:var(--text-secondary)] truncate flex items-center gap-2" data-testid="admin-thread-presence">
              <span className="truncate">{u.email}</span>
              <span>·</span>
              {presence.is_typing ? (
                <span className="flex items-center gap-1.5 text-[color:var(--bright-green)]" data-testid="admin-thread-presence-typing">
                  typing <TypingDots />
                </span>
              ) : seen ? (
                <span>last seen {seen}</span>
              ) : (
                <span className="opacity-70">not yet active</span>
              )}
            </div>
          </div>
          <MessageSquare className="h-4 w-4 text-[color:var(--orange)]" />
        </div>

        <div ref={scrollRef} className="max-h-[28rem] overflow-y-auto p-4 space-y-3" data-testid="admin-thread-messages">
          {msgs.length === 0 && (
            <p className="text-sm text-[color:var(--text-secondary)] text-center py-6">Empty conversation.</p>
          )}
          {msgs.map((m) => {
            const fromAdmin = m.sender === "admin";
            return (
              <div key={m.message_id} className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.message_id}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${fromAdmin ? "bg-[color:var(--fire-red)] text-white" : "bg-[color:var(--bg-secondary)] text-[color:var(--text-primary)]"}`}>
                  <div className={`text-[10px] uppercase tracking-[0.2em] mb-1 ${fromAdmin ? "text-white/70" : "text-[color:var(--text-secondary)]"}`}>
                    {fromAdmin ? (m.admin_name || "Trustee") : u.name}
                  </div>
                  <p className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</p>
                  <div className={`text-[10px] mt-1 ${fromAdmin ? "text-white/70" : "text-[color:var(--text-secondary)]"}`}>
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                  <TranslateButton messageId={m.message_id} theme={fromAdmin ? "dark" : "light"} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-black/5 p-4 space-y-2">
          <Textarea
            value={reply}
            onChange={onChangeReply}
            placeholder="Reply to the member…"
            rows={3}
            data-testid="admin-reply-input"
          />
          <div className="flex justify-end">
            <Button
              onClick={sendReply}
              disabled={sending || !reply.trim()}
              className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white"
              data-testid="admin-reply-send-btn"
            >
              <Send className="h-4 w-4 mr-1.5" />{sending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
